import * as React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { Box, IconButton, Typography } from '@mui/material'
import { CloseRounded } from '@mui/icons-material'
import { TypographyVariants } from '@/app/styles/themes/typography'
import { lightPalette } from '@/app/styles/themes/colors'

export type Actions = {
	text: string
	variant: 'outlined' | 'contained' | 'bespoke'
	color:
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'success'
	| 'error'
	| 'info'
	| 'warning'
	onClick(): void
	disabled?: boolean
}

interface PopupProps {
	title: string
	message?: string
	bodyContent?: React.ReactNode
	actions?: Actions[]
	isOpen: boolean
	onClose(): void
	hideCloseIcon?: boolean
}
/**
 * PopUp component renders a modal dialog with a title, message, and actions.
 * It uses Material-UI's Dialog component to display the popup.
 *
 * @component
 * @param {PopupProps} props - The properties for the PopUp component.
 * @param {string} props.title - The title of the popup.
 * @param {string} props.message - The message content of the popup.
 * @param {Array<Actions>} props.actions - The actions to be displayed in the popup.
 * @param {boolean} props.isOpen - A boolean to control the visibility of the popup.
 * @param {function} props.onClose - The function to call when the popup is closed.
 *
 * @returns {JSX.Element} The rendered PopUp component.
 *
 * @example
 * <PopUp
 *   title="Alert"
 *   message="This is an alert message."
 *   actions={[
 *     { text: 'Cancel', onClick: handleCancel, variant: 'outlined', color: 'primary' },
 *     { text: 'Confirm', onClick: handleConfirm, variant: 'contained', color: 'secondary' }
 *   ]}
 *   isOpen={isOpen}
 *   onClose={handleClose}
 * />
 */
export default function PopUp({
	title,
	message = '',
	bodyContent = '',
	actions,
	isOpen,
	onClose,
	hideCloseIcon = false,
}: Readonly<PopupProps>) {
	const hasDualActions = (actions?.length ?? 0) > 1

	const maxTextLength =
		actions?.reduce((max, action) => Math.max(max, action.text.length), 0) ?? 0
	const buttonWidth = maxTextLength * 8 + 32
	return (
		<Dialog
			fullWidth
			maxWidth="sm"
			open={isOpen}
			style={{ backgroundColor: '#40000000' }}
			data-testid="boss-dialog"
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			sx={{
				'& .MuiDialog-paper': {
					p: { xs: 3, sm: '20px' },
					m: 0,
				},
			}}
		>
			<DialogTitle
				id="alert-dialog-title"
				variant={TypographyVariants.mediumSemiBold}
				sx={{
					p: 0,
					py: { xs: 1, sm: 0 },
					display: 'flex',
					alignItems: 'center',
					color: lightPalette.foreground.neutral.onSurface,
				}}
			>
				{title}
				{!hideCloseIcon && (
					<IconButton
						data-testid="Close-dialog-button"
						sx={{ ml: 'auto', float: 'right', p: '6px' }}
						onClick={onClose}
					>
						<CloseRounded fontSize="small" />
					</IconButton>
				)}
			</DialogTitle>
			<DialogContent sx={{ py: '16px !important', px: 0 }}>
				<DialogContentText id="alert-dialog-description" sx={{ lineHeight: 0 }}>
					{message.length > 0 && (
						<Typography
							variant={TypographyVariants.baseRegular}
							sx={{ color: lightPalette.foreground.neutral.onSurface }} // was themeVars?.onSurface
						>
							{message.split('\n').map((line, idx) => (
								<React.Fragment key={`${line}-${idx}`}>
									{line}
									<br />
								</React.Fragment>
							))}
						</Typography>
					)}
					{bodyContent}
				</DialogContentText>
			</DialogContent>
			<DialogActions
				disableSpacing
				sx={{
					p: 0,
					py: { xs: 1, sm: 0 },
					justifyContent: { xs: 'space-between', sm: 'flex-end' },
					...(hasDualActions && { gap: { xs: 3, sm: 2 } }),
				}}
			>
				{actions != null ? (
					actions.map((child: Actions) => {
						return (
							<Button
								data-testid={`${child.text}-dialog-button`}
								size="medium"
								variant={child.variant}
								color={child.color}
								key={child.text}
								onClick={child.onClick}
								autoFocus
								disabled={child.disabled}
								sx={{
									maxWidth: `${buttonWidth}px`,
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									fontSize: '16px',
								}}
							>
								{child.text}
							</Button>
						)
					})
				) : (
					<Box />
				)}
			</DialogActions>
		</Dialog>
	)
}
